import { marketingHref } from "@/components/marketing/marketingLinks";
import Box from "@mui/material/Box";
import type { SxProps, Theme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";

import { StyledAnchor } from "@/components/common/styles";
import {
  MID,
  NOT_WIDE,
  TINY,
  marketingPalette,
} from "@/components/marketing/tokens";
import HomeTabs from "./HomeTabs";
import { ActionButton, SectionContainer } from "./HomePrimitives";
import { USE_CASES } from "./homeContent";
import { BODY_SMALL, HEADING_2 } from "./homeType";

/** The tinted tray the pills sit in. */
const TAB_LIST = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  p: "8px",
  borderRadius: "1rem",
  backgroundColor: "rgba(233, 238, 241, 0.6)",
  [NOT_WIDE]: {
    gap: "1rem",
    flexWrap: "wrap",
    justifyContent: "flex-start",
  },
  [MID]: { gap: "0.75rem" },
  [TINY]: { gap: "8px", width: 1, overflowX: "scroll" },
} satisfies SxProps<Theme>;

/** Six of these values move with the selection; the rest are the pill. */
const tabSx = (selected: boolean): SxProps<Theme> => ({
  flex: "0 0 auto",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  p: "10.08px 16px",
  borderRadius: "10.08px",
  fontFamily: "inherit",
  fontSize: "0.88rem",
  // `ButtonBase` sets its own leading; the published tab keeps the body's.
  lineHeight: 1.5,
  fontWeight: selected ? 500 : 400,
  color: marketingPalette.ink,
  backgroundColor: selected ? "#FFFFFF" : "transparent",
  border: `1px solid ${selected ? "rgba(4, 30, 57, 0.2)" : marketingPalette.pillRule}`,
  boxShadow: selected ? "0 4px 11px -5px rgba(0, 0, 0, 0.2)" : "none",
  "&:hover": {
    backgroundColor: selected ? "#FFFFFF" : marketingPalette.pillHover,
  },
  [NOT_WIDE]: {
    px: "8px",
    borderColor: selected ? marketingPalette.inkMuted : "#C6D0D5",
  },
  [TINY]: { p: "6.4px", fontSize: "0.81rem" },
});

/** The frame all five panes share. */
const PANEL_FRAME = {
  width: 1,
  mt: "1rem",
  p: "4rem",
  borderRadius: "1rem",
  border: "1px solid #F0F5F7",
  background: "linear-gradient(90deg, rgba(255, 255, 255, 0) 20%, #F0F5F8)",
  [NOT_WIDE]: { p: "3rem" },
  // The published pane keeps the narrow inset from the card step down.
  [MID]: { p: "1.8rem" },
} satisfies SxProps<Theme>;

export default function UseCaseSection() {
  return (
    <SectionContainer padding="bottom">
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "2rem",
        }}
      >
        <Typography component="h2" sx={HEADING_2}>
          {USE_CASES.heading}
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: 1,
          }}
        >
          <HomeTabs
            idPrefix="home-use-case"
            listSx={TAB_LIST}
            tabSx={tabSx}
            panelsWrapperSx={PANEL_FRAME}
            labels={USE_CASES.panes.map((pane) => pane.label)}
            panels={USE_CASES.panes.map((pane, index) => (
              <Box
                key={pane.label}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "3rem",
                  width: 1,
                  [NOT_WIDE]: {
                    flexDirection: "column",
                    alignItems: "flex-start",
                    gap: "2rem",
                  },
                  [MID]: { gap: "1rem" },
                  // The picture comes first on the smallest step, and the pane's
                  // own title is dropped, the selected tab is already carrying it.
                  [TINY]: { flexDirection: "column-reverse" },
                }}
              >
                <Box>
                  <Box
                    sx={{
                      // `.home-case-tab-pane-left_top`: the 27rem cap is the
                      // inner block's, inside a column with no width of its
                      // own, so the row shares its shrink the published way.
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      gap: "1rem",
                      width: 1,
                      maxWidth: "27rem",
                      textAlign: "left",
                      [NOT_WIDE]: { maxWidth: "none" },
                      [TINY]: { alignItems: "stretch" },
                    }}
                  >
                    <Box
                      sx={{
                        color: marketingPalette.ink,
                        fontSize: "1.5rem",
                        fontWeight: 600,
                        lineHeight: "110%",
                        [MID]: { fontSize: "1.3rem" },
                        [TINY]: { display: "none" },
                      }}
                    >
                      {pane.label}
                    </Box>
                    <Typography component="p" sx={BODY_SMALL}>
                      {pane.body}
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                      {pane.links.map((link) => (
                        <StyledAnchor
                          key={link.href}
                          href={marketingHref(link.href)}
                          sx={{
                            p: "8px 12px",
                            borderRadius: "8px",
                            border: `1px solid ${marketingPalette.outline}`,
                            backgroundColor: "background.default",
                            fontSize: 12.96,
                            fontWeight: 500,
                            lineHeight: "18.144px",
                            // The published chip paints its label through an inner block in the
                            // muted ink, one step lighter than the link around it.
                            color: marketingPalette.inkMuted,
                            "&:hover": {
                              borderColor: marketingPalette.inkSubtle,
                            },
                          }}
                        >
                          {link.label}
                        </StyledAnchor>
                      ))}
                    </Box>
                    <ActionButton
                      cta={pane.cta}
                      width={index === 0 ? "fixed" : "hug"}
                    />
                  </Box>
                </Box>
                <Box
                  component="img"
                  src={pane.image}
                  alt=""
                  loading="lazy"
                  sx={{
                    width: 1,
                    maxWidth: "36rem",
                    aspectRatio: "2.919 / 2",
                    objectFit: "fill",
                    [NOT_WIDE]: { maxWidth: "none" },
                  }}
                />
              </Box>
            ))}
          />
        </Box>
      </Box>
    </SectionContainer>
  );
}
